export class AudioSys {
  ctx: AudioContext | null = null;
  master!: GainNode;
  sfx!: GainNode;
  amb!: GainNode;
  private buffers = new Map<string, AudioBuffer>();
  private rain: AudioBufferSourceNode | null = null;
  private hum: AudioBufferSourceNode | null = null;
  private started = false;
  listenerX = 0;
  listenerY = 0;
  listenerZ = 0;

  async unlock() {
    if (this.started) return;
    const ctx = new AudioContext();
    this.ctx = ctx;
    this.master = ctx.createGain();
    this.master.gain.value = 0.85;
    this.master.connect(ctx.destination);
    this.sfx = ctx.createGain();
    this.sfx.gain.value = 0.95;
    this.sfx.connect(this.master);
    this.amb = ctx.createGain();
    this.amb.gain.value = 0.42;
    this.amb.connect(this.master);
    await ctx.resume();
    this.bake();
    this.startAmbience();
    this.started = true;
  }

  setListener(x: number, y: number, z: number) {
    this.listenerX = x;
    this.listenerY = y;
    this.listenerZ = z;
    if (!this.ctx) return;
    const l = this.ctx.listener;
    if (l.positionX) {
      l.positionX.value = x;
      l.positionY.value = y;
      l.positionZ.value = z;
    }
  }

  play(name: string, opts: { vol?: number; rate?: number; x?: number; y?: number; z?: number } = {}) {
    if (!this.ctx || !this.started) return;
    const buf = this.buffers.get(name);
    if (!buf) return;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = (opts.rate ?? 1) * (0.97 + Math.random() * 0.06);
    const g = this.ctx.createGain();
    g.gain.value = opts.vol ?? 1;
    if (opts.x !== undefined) {
      const p = this.ctx.createPanner();
      p.panningModel = "HRTF";
      p.distanceModel = "inverse";
      p.refDistance = 4;
      p.maxDistance = 80;
      p.rolloffFactor = 1.1;
      p.positionX.value = opts.x;
      p.positionY.value = opts.y ?? 1.5;
      p.positionZ.value = opts.z ?? 0;
      src.connect(g);
      g.connect(p);
      p.connect(this.sfx);
    } else {
      src.connect(g);
      g.connect(this.sfx);
    }
    src.start();
  }

  private startAmbience() {
    if (!this.ctx) return;
    const rain = this.ctx.createBufferSource();
    rain.buffer = this.buffers.get("rain")!;
    rain.loop = true;
    const rg = this.ctx.createGain();
    rg.gain.value = 0.55;
    rain.connect(rg);
    rg.connect(this.amb);
    rain.start();
    this.rain = rain;

    const hum = this.ctx.createBufferSource();
    hum.buffer = this.buffers.get("hum")!;
    hum.loop = true;
    const hg = this.ctx.createGain();
    hg.gain.value = 0.22;
    hum.connect(hg);
    hg.connect(this.amb);
    hum.start();
    this.hum = hum;
  }

  private bake() {
    const c = this.ctx!;
    const sr = c.sampleRate;
    const mk = (n: number, fn: (i: number, t: number) => number) => {
      const b = c.createBuffer(1, n, sr);
      const d = b.getChannelData(0);
      for (let i = 0; i < n; i++) d[i] = fn(i, i / sr);
      return b;
    };
    const noise = () => Math.random() * 2 - 1;

    this.buffers.set(
      "ar",
      mk((sr * 0.28) | 0, (i, t) => {
        const env = Math.exp(-t * 28) * (t < 0.012 ? t / 0.012 : 1);
        const thump = Math.sin(2 * Math.PI * 78 * t) * Math.exp(-t * 40);
        const crack = noise() * Math.exp(-t * 55);
        const mid = Math.sin(2 * Math.PI * (420 + t * 900) * t) * Math.exp(-t * 36);
        return (thump * 0.55 + crack * 0.7 + mid * 0.28) * env * 0.95;
      })
    );
    this.buffers.set(
      "smg",
      mk((sr * 0.16) | 0, (i, t) => {
        const env = Math.exp(-t * 42);
        return (noise() * 0.65 + Math.sin(2 * Math.PI * 520 * t) * 0.25 + Math.sin(2 * Math.PI * 90 * t) * 0.35) * env;
      })
    );
    this.buffers.set(
      "pistol",
      mk((sr * 0.22) | 0, (i, t) => {
        const env = Math.exp(-t * 24);
        return (noise() * 0.55 + Math.sin(2 * Math.PI * 110 * t) * 0.5 + Math.sin(2 * Math.PI * 680 * t) * 0.2) * env;
      })
    );
    this.buffers.set(
      "shot",
      mk((sr * 0.42) | 0, (i, t) => {
        const env = Math.exp(-t * 14);
        const body = noise() * (t < 0.04 ? 1 : Math.exp(-(t - 0.04) * 18));
        const low = Math.sin(2 * Math.PI * 62 * t) * Math.exp(-t * 18);
        return (body * 0.8 + low * 0.55) * env;
      })
    );
    this.buffers.set(
      "reload",
      mk((sr * 0.55) | 0, (i, t) => {
        const click = (tt: number, f: number) => Math.sin(2 * Math.PI * f * tt) * Math.exp(-tt * 60);
        let s = 0;
        if (t < 0.08) s += click(t, 1800) * 0.4 + noise() * 0.08 * Math.exp(-t * 40);
        if (t > 0.18 && t < 0.32) s += click(t - 0.18, 900) * 0.5 + noise() * 0.12;
        if (t > 0.4 && t < 0.5) s += click(t - 0.4, 2200) * 0.55;
        return s;
      })
    );
    this.buffers.set(
      "step",
      mk((sr * 0.12) | 0, (i, t) => {
        const env = Math.exp(-t * 38);
        return (noise() * 0.55 + Math.sin(2 * Math.PI * 70 * t) * 0.25) * env * 0.45;
      })
    );
    this.buffers.set(
      "hit",
      mk((sr * 0.08) | 0, (i, t) => Math.sin(2 * Math.PI * 1400 * t) * Math.exp(-t * 70) * 0.35)
    );
    this.buffers.set(
      "head",
      mk((sr * 0.1) | 0, (i, t) => (Math.sin(2 * Math.PI * 2100 * t) + noise() * 0.3) * Math.exp(-t * 55) * 0.4)
    );
    this.buffers.set(
      "hurt",
      mk((sr * 0.25) | 0, (i, t) => (noise() * 0.3 + Math.sin(2 * Math.PI * 180 * t) * 0.5) * Math.exp(-t * 14) * 0.5)
    );
    this.buffers.set(
      "ui",
      mk((sr * 0.06) | 0, (i, t) => Math.sin(2 * Math.PI * 880 * t) * Math.exp(-t * 40) * 0.2)
    );
    this.buffers.set(
      "ui2",
      mk((sr * 0.09) | 0, (i, t) => Math.sin(2 * Math.PI * 1240 * t) * Math.exp(-t * 28) * 0.22)
    );
    this.buffers.set(
      "explode",
      mk((sr * 1.1) | 0, (i, t) => {
        const env = Math.exp(-t * 4.5);
        return (noise() * Math.exp(-t * 6) * 0.85 + Math.sin(2 * Math.PI * 42 * t) * Math.exp(-t * 5)) * env;
      })
    );
    this.buffers.set(
      "plant",
      mk((sr * 0.15) | 0, (i, t) => Math.sin(2 * Math.PI * (640 + t * 400) * t) * Math.exp(-t * 18) * 0.3)
    );
    this.buffers.set(
      "rico",
      mk((sr * 0.14) | 0, (i, t) => (Math.sin(2 * Math.PI * (3200 - t * 8000) * t) + noise() * 0.2) * Math.exp(-t * 30) * 0.28)
    );
    this.buffers.set(
      "rain",
      mk(sr * 4, () => {
        let s = 0;
        for (let k = 0; k < 6; k++) s += noise();
        return (s / 6) * 0.22;
      })
    );
    this.buffers.set(
      "hum",
      mk(sr * 2, (i, t) => {
        return (
          Math.sin(2 * Math.PI * 55 * t) * 0.15 +
          Math.sin(2 * Math.PI * 110.2 * t) * 0.08 +
          noise() * 0.02
        );
      })
    );
    this.buffers.set(
      "whoosh",
      mk((sr * 0.2) | 0, (i, t) => noise() * Math.exp(-t * 12) * (t < 0.04 ? t / 0.04 : 1) * 0.3)
    );
  }
}
